import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    type ActiveRoleAssignmentResponse,
    ApiError,
    formatTimestampForMerchantUi,
    GetOrganizationResponseSchema,
    type GetOrganizationResponse,
    type GetReceiptDataResponse,
    type GetTransactionDetailsResponse,
} from '@pillar-portal/api-types';
import { hasOrganizationsPage, hasPaymentsInfrastructurePage } from '@pillar-portal/api-types';
import { z } from 'zod';
import { authApi, authorizationApi, organizationApi, paymentsApi } from '@/lib/pillarApi';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { formatMonetaryAmount, runInBackground } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BackButton } from '@/components/ui/back-button';

type TransactionDetailMode = 'payment-transactions' | 'card-vault';

interface TransactionDetailContentProps {
    mode: TransactionDetailMode;
}

function resolveReceiptErrorMessage(error: unknown): string {
    if (error instanceof Error) {
        const message = error.message.trim();
        if (message.length > 0) {
            return message;
        }
        return error.name;
    }
    const errorAsStringParseResult = z.string().safeParse(error);
    if (errorAsStringParseResult.success) {
        const message = errorAsStringParseResult.data.trim();
        if (message.length > 0) {
            return message;
        }
    }
    try {
        const serialized = JSON.stringify(error);
        const serializedParseResult = z.string().safeParse(serialized);
        if (serializedParseResult.success && serializedParseResult.data.length > 0) {
            return serializedParseResult.data;
        }
    } catch {
        // Ignore JSON serialization errors.
    }
    return 'Failed to open receipt';
}

function formatMerchantTimestamp(
    value: string,
    merchantTimeZone: string | undefined
): string {
    if (merchantTimeZone === undefined) {
        return value;
    }
    return formatTimestampForMerchantUi(value, merchantTimeZone);
}

export function TransactionDetailContent({ mode }: TransactionDetailContentProps) {
    const navigate = useNavigate();
    const log = createComponentLogger('TransactionDetailContent');
    const params = useParams();
    const [isGeneratingReceipt, setIsGeneratingReceipt] = useState<boolean>(false);
    const [isRefreshingGatewayDetails, setIsRefreshingGatewayDetails] = useState<boolean>(false);
    const [receiptError, setReceiptError] = useState<string | null>(null);
    const [receiptSuccessMessage, setReceiptSuccessMessage] = useState<string | null>(null);
    const [gatewayDetailsRefreshError, setGatewayDetailsRefreshError] = useState<string | null>(null);
    const [gatewayDetailsRefreshSuccessMessage, setGatewayDetailsRefreshSuccessMessage] =
        useState<string | null>(null);

    const transactionUuidParseResult = z.string().uuid().safeParse(params.id);
    if (!transactionUuidParseResult.success) {
        throw new Error(`Invalid transaction UUID: ${params.id}`);
    }
    const transactionUuid: string = transactionUuidParseResult.data;

    const isCardVaultMode: boolean = mode === 'card-vault';
    const listPath: '/payment-transactions' | '/card-vault' = isCardVaultMode
        ? '/card-vault'
        : '/payment-transactions';
    const listTooltip: string = isCardVaultMode ? 'Back to Card Vault' : 'Back to Payment Transactions';

    const { data: activeRoleAssignment, isLoading: isRoleLoading } = useQuery<
        ActiveRoleAssignmentResponse | null
    >({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        staleTime: 30000,
    });

    const {
        data: transactionDetails,
        isLoading: isLoadingTransaction,
        error: transactionError,
        refetch: refetchTransactionDetails,
    } = useQuery<GetTransactionDetailsResponse>({
        queryKey: ['paymentTransactionDetails', transactionUuid],
        queryFn: async () => {
            return await paymentsApi.getTransactionDetails(transactionUuid);
        },
    });

    const { data: merchantOrganization } = useQuery<GetOrganizationResponse>({
        queryKey: [
            'transactionDetailMerchantOrganization',
            transactionDetails?.merchant_organization_uuid,
        ],
        queryFn: async () => {
            if (transactionDetails === undefined) {
                throw new Error('Transaction details are required before loading organization');
            }
            const response = await organizationApi.getOrganization(
                transactionDetails.merchant_organization_uuid
            );
            return GetOrganizationResponseSchema.parse(response);
        },
        enabled: transactionDetails !== undefined,
        staleTime: 30000,
    });

    const {
        data: receiptData,
        isLoading: isLoadingReceiptData,
        error: receiptDataError,
        refetch: refetchReceiptData,
    } = useQuery<GetReceiptDataResponse>({
        queryKey: ['paymentTransactionReceiptData', transactionUuid],
        queryFn: async () => {
            return await paymentsApi.getReceiptData(transactionUuid);
        },
        enabled: !isCardVaultMode && transactionDetails !== undefined,
    });
    const { data: merchantOrganizationAccess } = useQuery<{ has_access: boolean }>({
        queryKey: [
            'transactionDetailMerchantOrganizationAccess',
            merchantOrganization?.organization_uuid,
        ],
        queryFn: async () => {
            if (merchantOrganization?.organization_uuid === undefined) {
                throw new Error('Organization UUID is required before checking organization access');
            }
            return await authorizationApi.hasAccessToOrganization(
                merchantOrganization.organization_uuid
            );
        },
        enabled: merchantOrganization?.organization_uuid !== undefined,
        staleTime: 30000,
    });

    const canGetReceipt: boolean = receiptData?.can_get_receipt === true;
    const missingReceiptFields: ReadonlyArray<string> = receiptData?.missing_required_fields ?? [];
    const resolvedTransactionErrorMessage: string | null =
        transactionError === null || transactionError === undefined
            ? null
            : transactionError instanceof ApiError && transactionError.status === 403
              ? 'Access denied.'
              : transactionError instanceof Error
              ? transactionError.message
              : 'Failed to load transaction details';
    const resolvedReceiptErrorMessage: string | null =
        receiptDataError === null || receiptDataError === undefined
            ? null
            : receiptDataError instanceof Error
              ? receiptDataError.message
              : 'Failed to load receipt readiness';

    const lifecycleEvents = useMemo(() => {
        return transactionDetails?.lifecycle_events ?? [];
    }, [transactionDetails]);
    const parsedIpwTransactionDetails = useMemo(() => {
        const value = transactionDetails?.ipw_transaction_details;
        const parseResult = z.record(z.string(), z.unknown()).safeParse(value);
        return parseResult.success ? parseResult.data : null;
    }, [transactionDetails?.ipw_transaction_details]);
    const customerNameParseResult = z
        .string()
        .min(1)
        .safeParse(parsedIpwTransactionDetails?.customerName);
    const customerNameFromTransactionDetails = customerNameParseResult.success
        ? customerNameParseResult.data.trim()
        : null;

    const handleGetReceipt = async (): Promise<void> => {
        const logPrefix = '[TransactionDetailContent][GetReceipt]';
        const startedAtMs = Date.now();
        console.info(`${logPrefix} clicked`, {
            payment_transaction_uuid: transactionDetails?.payment_transaction_uuid,
            canGetReceipt,
        });
        if (transactionDetails === undefined || !canGetReceipt) {
            console.warn(`${logPrefix} aborted: transactionDetails missing or canGetReceipt=false`, {
                hasTransactionDetails: transactionDetails !== undefined,
                canGetReceipt,
            });
            return;
        }
        setReceiptError(null);
        setReceiptSuccessMessage(null);
        setIsGeneratingReceipt(true);
        console.info(`${logPrefix} opening popup`);
        const popup = window.open('about:blank', '_blank');
        try {
            if (popup === null) {
                console.error(`${logPrefix} popup creation returned null`);
                throw new Error('Popup blocked while opening receipt');
            }
            console.info(`${logPrefix} popup opened; building receipt HTML URL`, {
                payment_transaction_uuid: transactionDetails.payment_transaction_uuid,
            });
            const receiptHtmlUrl = paymentsApi.getReceiptHTMLUrl(
                transactionDetails.payment_transaction_uuid
            );
            console.info(`${logPrefix} navigating popup to receipt URL`, {
                receipt_html_url: receiptHtmlUrl,
                elapsed_ms: Date.now() - startedAtMs,
            });
            popup.location.href = receiptHtmlUrl;
            console.info(`${logPrefix} popup navigation initiated`, {
                elapsed_ms: Date.now() - startedAtMs,
            });
            setReceiptSuccessMessage('Receipt opened in a new browser tab.');
        } catch (error: unknown) {
            popup?.close();
            console.error('[TransactionDetailContent] Failed to open receipt', error);
            console.error(`${logPrefix} failed`, {
                elapsed_ms: Date.now() - startedAtMs,
                error,
            });
            const message: string = resolveReceiptErrorMessage(error);
            setReceiptError(message);
        } finally {
            console.info(`${logPrefix} done`, {
                elapsed_ms: Date.now() - startedAtMs,
            });
            setIsGeneratingReceipt(false);
        }
    };

    const handleRefreshGatewayDetails = async (): Promise<void> => {
        if (transactionDetails === undefined) {
            return;
        }
        setGatewayDetailsRefreshError(null);
        setGatewayDetailsRefreshSuccessMessage(null);
        setIsRefreshingGatewayDetails(true);
        try {
            const paymentTransactionUuid = transactionDetails.payment_transaction_uuid;
            await paymentsApi.refreshGatewayTransactionDetails(
                paymentTransactionUuid
            );
            await refetchTransactionDetails();
            if (!isCardVaultMode) {
                await refetchReceiptData();
            }
            setGatewayDetailsRefreshSuccessMessage('Gateway details refreshed successfully.');
        } catch (error: unknown) {
            const message: string =
                error instanceof Error && error.message.trim().length > 0
                    ? error.message
                    : 'Failed to refresh gateway details';
            setGatewayDetailsRefreshError(message);
        } finally {
            setIsRefreshingGatewayDetails(false);
        }
    };

    if (isRoleLoading) {
        return <div>Loading...</div>;
    }

    if (isLoadingTransaction) {
        return <div>Loading transaction details...</div>;
    }

    if (resolvedTransactionErrorMessage !== null || transactionDetails === undefined) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    {resolvedTransactionErrorMessage ?? 'Transaction details not found'}
                </AlertDescription>
            </Alert>
        );
    }

    const cardTitle = `${transactionDetails.card_brand ?? 'Card'} xxxx${transactionDetails.card_last4 ?? '----'}`;
    const merchantOrganizationLabel =
        transactionDetails.merchant_organization_display_name ?? 'Unknown merchant organization';
    const terminalDeviceLabel =
        transactionDetails.terminal_device_name?.trim().length
            ? transactionDetails.terminal_device_name
            : transactionDetails.processor_device_id;
    const hasMerchantOrganizationLink: boolean =
        hasOrganizationsPage(activeRoleAssignment ?? null) &&
        merchantOrganizationAccess?.has_access === true;
    const hasTerminalDeviceLink: boolean =
        hasPaymentsInfrastructurePage(activeRoleAssignment ?? null) &&
        merchantOrganizationAccess?.has_access === true;
    const isRefreshGatewayDetailsDisabled: boolean = isRefreshingGatewayDetails;
    const refreshGatewayDetailsDisabledReason: string | undefined =
        isRefreshingGatewayDetails ? 'Gateway details refresh in progress' : undefined;

    return (
        <div className="space-y-6">
            <BackButton
                href={listPath}
                tooltip={listTooltip}
                onClick={() => {
                    runInBackground(navigate(listPath), (error: unknown) => {
                        log.error('Failed to navigate back to transaction list', error);
                    });
                }}
            />

            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">
                        {isCardVaultMode
                            ? cardTitle
                            : `Transaction ${transactionDetails.merchant_transaction_id}`}
                    </h1>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-3">
                    <Button
                        onClick={() => {
                            runInBackground(handleRefreshGatewayDetails(), (error: unknown) => {
                                log.error('Failed to refresh gateway details', error);
                            });
                        }}
                        disabled={isRefreshGatewayDetailsDisabled}
                        title={refreshGatewayDetailsDisabledReason}
                        variant="outline"
                    >
                        {isRefreshingGatewayDetails
                            ? 'Refreshing Gateway Details...'
                            : 'Refresh Gateway Details'}
                    </Button>
                    {!isCardVaultMode && (
                        <Button
                            onClick={() => {
                                runInBackground(handleGetReceipt(), (error: unknown) => {
                                    log.error('Failed to execute receipt generation workflow', error);
                                });
                            }}
                            disabled={
                                isGeneratingReceipt ||
                                isLoadingReceiptData ||
                                resolvedReceiptErrorMessage !== null ||
                                !canGetReceipt
                            }
                        >
                            {isGeneratingReceipt ? 'Generating Receipt...' : 'Get Receipt'}
                        </Button>
                    )}
                </div>
            </div>

            {gatewayDetailsRefreshSuccessMessage !== null && (
                <Alert>
                    <AlertDescription>{gatewayDetailsRefreshSuccessMessage}</AlertDescription>
                </Alert>
            )}
            {gatewayDetailsRefreshError !== null && (
                <Alert variant="destructive">
                    <AlertDescription>{gatewayDetailsRefreshError}</AlertDescription>
                </Alert>
            )}
            {!isCardVaultMode && resolvedReceiptErrorMessage !== null && (
                <Alert variant="destructive">
                    <AlertDescription>{resolvedReceiptErrorMessage}</AlertDescription>
                </Alert>
            )}
            {!isCardVaultMode &&
                !isLoadingReceiptData &&
                resolvedReceiptErrorMessage === null &&
                !canGetReceipt &&
                missingReceiptFields.length > 0 && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            <div className="space-y-2">
                                <div className="font-semibold">
                                    Receipt unavailable: missing required fields
                                </div>
                                <ul className="list-disc list-inside">
                                    {missingReceiptFields.map((fieldName) => (
                                        <li key={fieldName}>{fieldName}</li>
                                    ))}
                                </ul>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}
            {!isCardVaultMode && receiptSuccessMessage !== null && (
                <Alert>
                    <AlertDescription>{receiptSuccessMessage}</AlertDescription>
                </Alert>
            )}
            {!isCardVaultMode && receiptError !== null && (
                <Alert variant="destructive">
                    <AlertDescription>{receiptError}</AlertDescription>
                </Alert>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div>
                        <span className="font-semibold">Anchorbase TXID:</span>{' '}
                        {transactionDetails.merchant_transaction_id}
                    </div>
                    <div>
                        <span className="font-semibold">Status:</span>{' '}
                        <Badge
                            variant={
                                transactionDetails.status === 'FAILED' ? 'destructive' : 'default'
                            }
                        >
                            {transactionDetails.status}
                        </Badge>
                    </div>
                    <div>
                        <span className="font-semibold">Reconciled:</span> {transactionDetails.reconciled}
                    </div>
                    <div>
                        <span className="font-semibold">Merchant Organization:</span>{' '}
                        {hasMerchantOrganizationLink ? (
                            <a
                                className="text-blue-700 underline"
                                href={`/organizations/${transactionDetails.merchant_organization_uuid}`}
                            >
                                {merchantOrganizationLabel}
                            </a>
                        ) : (
                            merchantOrganizationLabel
                        )}
                    </div>
                    <div>
                        <span className="font-semibold">Terminal Device:</span>{' '}
                        {hasTerminalDeviceLink ? (
                            <a
                                className="text-blue-700 underline"
                                href={`/payments-infrastructure/terminal-devices/${transactionDetails.terminal_device_uuid}`}
                            >
                                {terminalDeviceLabel}
                            </a>
                        ) : (
                            terminalDeviceLabel
                        )}
                    </div>
                    <div>
                        <span className="font-semibold">Processor TID:</span>{' '}
                        {transactionDetails.processor_tid ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Gateway ID:</span> {transactionDetails.gateway_id}
                    </div>
                    <div>
                        <span className="font-semibold">Gateway Transaction Type:</span>{' '}
                        {transactionDetails.gateway_transaction_type}
                    </div>
                    <div>
                        <span className="font-semibold">Currency:</span>{' '}
                        {transactionDetails.currency_code ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Customer:</span>{' '}
                        {customerNameFromTransactionDetails ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Cardholder:</span>{' '}
                        {transactionDetails.cardholder_name ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Card Brand:</span>{' '}
                        {transactionDetails.card_brand ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Card Type:</span>{' '}
                        {transactionDetails.card_type ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Last4:</span>{' '}
                        {transactionDetails.card_last4 ?? '—'}
                    </div>
                    {!isCardVaultMode && (
                        <>
                            <div>
                                <span className="font-semibold">Original Amount:</span>{' '}
                                <span className="monetary-amount inline-block min-w-[8ch]">
                                    {formatMonetaryAmount(
                                        transactionDetails.original_amount,
                                        transactionDetails.currency_code ?? 'USD'
                                    )}
                                </span>
                            </div>
                            <div>
                                <span className="font-semibold">Batched Amount:</span>{' '}
                                {transactionDetails.batched_amount !== null
                                    ? (
                                          <span className="monetary-amount inline-block min-w-[8ch]">
                                              {formatMonetaryAmount(
                                                  transactionDetails.batched_amount,
                                                  transactionDetails.currency_code ?? 'USD'
                                              )}
                                          </span>
                                      )
                                    : '—'}
                            </div>
                            <div>
                                <span className="font-semibold">Refunded Amount:</span>{' '}
                                {transactionDetails.refunded_amount !== null ? (
                                    <span className="monetary-amount inline-block min-w-[8ch]">
                                        {formatMonetaryAmount(
                                            transactionDetails.refunded_amount,
                                            transactionDetails.currency_code ?? 'USD'
                                        )}
                                    </span>
                                ) : (
                                    '—'
                                )}
                            </div>
                            <div>
                                <span className="font-semibold">Amount Breakdown:</span>{' '}
                                {transactionDetails.amount_breakdown !== undefined &&
                                transactionDetails.amount_breakdown !== null ? (
                                    <pre className="mt-1 text-xs bg-gray-50 p-2 rounded border overflow-x-auto overflow-y-auto">
                                        {JSON.stringify(transactionDetails.amount_breakdown, null, 2)}
                                    </pre>
                                ) : (
                                    '—'
                                )}
                            </div>
                        </>
                    )}
                    {!isCardVaultMode && (
                        <div>
                            <span className="font-semibold">Gateway Txn ID:</span>{' '}
                            {transactionDetails.gateway_txn_id ?? '—'}
                        </div>
                    )}
                    {!isCardVaultMode && (
                        <div>
                            <span className="font-semibold">Gateway Auth Code:</span>{' '}
                            {transactionDetails.gateway_auth_code ?? '—'}
                        </div>
                    )}
                    {!isCardVaultMode && (
                        <div>
                            <span className="font-semibold">Gateway RRN:</span>{' '}
                            {transactionDetails.gateway_rrn ?? '—'}
                        </div>
                    )}
                    <div>
                        <span className="font-semibold">Gateway Batch:</span>{' '}
                        {transactionDetails.gateway_batch ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Gateway STAN:</span>{' '}
                        {transactionDetails.gateway_stan ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">IPW System ID:</span>{' '}
                        {transactionDetails.ipw_system_id}
                    </div>
                    <div>
                        <span className="font-semibold">IPW Customer ID:</span>{' '}
                        {transactionDetails.ipw_customer_id ?? '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Transaction Created At:</span>{' '}
                        {formatMerchantTimestamp(
                            transactionDetails.transaction_creation_ts,
                            transactionDetails.merchant_timezone
                        )}
                    </div>
                    <div>
                        <span className="font-semibold">Transaction Authorized At:</span>{' '}
                        {transactionDetails.transaction_auth_ts !== null &&
                        transactionDetails.transaction_auth_ts !== undefined
                            ? formatMerchantTimestamp(
                                  transactionDetails.transaction_auth_ts,
                                  transactionDetails.merchant_timezone
                              )
                            : '—'}
                    </div>
                    <div>
                        <span className="font-semibold">Transaction Configuration:</span>{' '}
                        {transactionDetails.transaction_configuration !== undefined ? (
                            <pre className="mt-1 text-xs bg-gray-50 p-2 rounded border overflow-x-auto overflow-y-auto">
                                {JSON.stringify(transactionDetails.transaction_configuration, null, 2)}
                            </pre>
                        ) : (
                            '—'
                        )}
                    </div>
                    {transactionDetails.ipw_transaction_details !== undefined &&
                        transactionDetails.ipw_transaction_details !== null && (
                            <div>
                                <span className="font-semibold">Transaction Details:</span>
                                <pre className="mt-1 text-xs bg-gray-50 p-2 rounded border overflow-x-auto overflow-y-auto">
                                    {JSON.stringify(transactionDetails.ipw_transaction_details, null, 2)}
                                </pre>
                            </div>
                        )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Lifecycle Events</CardTitle>
                </CardHeader>
                <CardContent>
                    {lifecycleEvents.length === 0 ? (
                        <div className="text-sm text-gray-600">No lifecycle events found.</div>
                    ) : (
                        <div className="space-y-4">
                            {lifecycleEvents.map((event) => (
                                <div
                                    key={event.payment_transaction_lifecycle_event_id}
                                    className="border rounded-md p-3 bg-white space-y-3"
                                >
                                    <div className="text-sm font-semibold">
                                        {event.event_type} #{event.payment_transaction_lifecycle_event_id}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        start=
                                        {formatMerchantTimestamp(
                                            event.event_start_ts,
                                            transactionDetails.merchant_timezone
                                        )}{' '}
                                        end=
                                        {formatMerchantTimestamp(
                                            event.event_end_ts,
                                            transactionDetails.merchant_timezone
                                        )}
                                    </div>
                                    {event.error_message !== undefined && (
                                        <div className="text-xs text-red-700 mt-1">
                                            {event.error_code ? `${event.error_code}: ` : ''}
                                            {event.error_message}
                                        </div>
                                    )}

                                    {event.pillar_request !== undefined && (
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="text-sm">pillar_request</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto overflow-y-auto">
                                                    {JSON.stringify(event.pillar_request, null, 2)}
                                                </pre>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {event.gateway_request !== undefined && (
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="text-sm">gateway_request</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto overflow-y-auto">
                                                    {JSON.stringify(event.gateway_request, null, 2)}
                                                </pre>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {event.gateway_response !== undefined && (
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="text-sm">gateway_response</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto overflow-y-auto">
                                                    {JSON.stringify(event.gateway_response, null, 2)}
                                                </pre>
                                            </CardContent>
                                        </Card>
                                    )}

                                    {event.pillar_response !== undefined && (
                                        <Card>
                                            <CardHeader>
                                                <CardTitle className="text-sm">pillar_response</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <pre className="text-xs bg-gray-50 p-3 rounded border overflow-x-auto overflow-y-auto">
                                                    {JSON.stringify(event.pillar_response, null, 2)}
                                                </pre>
                                            </CardContent>
                                        </Card>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
